import { useState, useEffect, useMemo } from 'react';
import {
    Box, Text, Flex, Grid, Progress, VStack, HStack, Spinner, Button,
    Icon, Badge, Input, InputGroup, InputLeftElement, SimpleGrid, Select, Divider,
} from '@chakra-ui/react';
import {
    FiActivity, FiSearch, FiZap, FiClock, FiCalendar, FiSmartphone, FiMonitor,
    FiGlobe, FiCheckCircle, FiUser,
} from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import API from '../../../helpers/axiosInstance';

const ACCENT = '#4f5ed1';
const PALETTE = ['#4f5ed1', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#14b8a6', '#3b82f6', '#ec4899', '#64748b'];

const Card = (props) => (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="white" p={5} {...props} />
);
const SectionHead = ({ title, sub }) => (
    <Box mb={4}>
        <Text fontWeight="700" fontSize="md">{title}</Text>
        {sub && <Text fontSize="sm" color="gray.500" mt={0.5}>{sub}</Text>}
    </Box>
);

const humanize = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtAmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const parseDT = (d) => (d ? new Date(String(d).replace(' ', 'T')) : null);
const fmtDateTime = (d) => { const dt = parseDT(d); return dt && !isNaN(dt) ? dt.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; };
const fmtDate = (d) => { const dt = parseDT(d); return dt && !isNaN(dt) ? dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; };
const fmtTime = (d) => { const dt = parseDT(d); return dt && !isNaN(dt) ? dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''; };
const fmtDur = (sec) => {
    sec = Math.max(0, Math.round(Number(sec) || 0));
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60), s = sec % 60;
    if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
    const h = Math.floor(m / 60); return `${h}h ${m % 60}m`;
};
// Real device from user_agent — no assumption when it's blank.
const deviceOf = (ua) => {
    const u = String(ua || '').toLowerCase();
    if (!u) return { label: 'Unknown', icon: FiActivity };
    if (/android/.test(u)) return { label: 'Mobile · Android', icon: FiSmartphone };
    if (/iphone|ipad|ios/.test(u)) return { label: 'Mobile · iOS', icon: FiSmartphone };
    if (/mobile/.test(u)) return { label: 'Mobile', icon: FiSmartphone };
    return { label: 'Desktop', icon: FiMonitor };
};
const srcColor = (name, i) => PALETTE[i % PALETTE.length];

const Kpi = ({ label, value, sub, subColor }) => (
    <Card minW="150px">
        <Text fontSize="sm" color="gray.500" fontWeight="600">{label}</Text>
        <Text fontSize="2xl" fontWeight="800" mt={2} lineHeight="1.1">{value}</Text>
        {sub && <Text fontSize="xs" color={subColor || 'gray.500'} mt={1} fontWeight={subColor ? '600' : '400'}>{sub}</Text>}
    </Card>
);

export function InteractionsTab({ contactId }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [q, setQ] = useState('');
    const [srcFilter, setSrcFilter] = useState('all');
    const [activeSession, setActiveSession] = useState(0);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        API.get(`${process.env.REACT_APP_API_URL}/contacts/${contactId}/interactions`, { headers: getApiHeaders() })
            // Accept either envelope: some contact endpoints wrap the payload in `data`,
            // others return it at the top level. Reading only `data.data` turned a valid
            // response into an indistinguishable "No data available".
            .then((r) => { if (alive) setData(r?.data?.data ?? r?.data ?? null); })
            .catch((e) => {
                console.error('interactions error:', e);
                if (!alive) return;
                setData(null);
                // Keep the real cause on screen instead of a toast that disappears after
                // 3s and leaves a bare "No data available" — the reported symptom was
                // indistinguishable from a contact that simply has no events.
                const status = e?.response?.status;
                setError({
                    status,
                    message: e?.response?.data?.message || e?.message || 'Request failed',
                });
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId, reloadKey]);

    const total = data?.total_events || 0;
    const bySource = data?.by_source || [];
    const ccy = data?.currency || '';
    const amtLabel = (n) => (ccy ? `${ccy} ${fmtAmt(n)}` : fmtAmt(n));

    // Month-over-month delta (real, from monthly counts).
    const mom = useMemo(() => {
        const m = data?.monthly || [];
        if (m.length < 2) return null;
        const cur = m[m.length - 1].count, prev = m[m.length - 2].count;
        if (!prev) return null;
        return Math.round(((cur - prev) / prev) * 100);
    }, [data]);

    // Group the recent detail stream into sessions for the Journey timeline (real).
    const sessionTimelines = useMemo(() => {
        const map = {};
        for (const e of (data?.detail || [])) {
            if (!e.session_id) continue;
            (map[e.session_id] = map[e.session_id] || []).push(e);
        }
        for (const k of Object.keys(map)) map[k].sort((a, b) => parseDT(a.date_time) - parseDT(b.date_time));
        return map;
    }, [data]);

    const sources = useMemo(() => ['all', ...bySource.map((s) => s.source)], [bySource]);
    const tableRows = useMemo(() => {
        const s = q.trim().toLowerCase();
        return (data?.events || []).filter((e) =>
            (srcFilter === 'all' || e.source === srcFilter) &&
            (!s || e.event.toLowerCase().includes(s)));
    }, [data, q, srcFilter]);

    if (loading) return <Flex justify="center" align="center" minH="400px"><Spinner size="xl" color={ACCENT} /></Flex>;
    if (error) {
        return (
            <Card mt={4} textAlign="center" py={12}>
                <Icon as={FiActivity} boxSize={8} color="red.300" />
                <Text mt={3} fontWeight="600">Couldn't load interactions</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                    {error.status ? `${error.status} — ` : ''}{error.message}
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                    GET /contacts/{contactId}/interactions
                </Text>
                <Button size="sm" mt={4} alignSelf="center" onClick={() => setReloadKey((k) => k + 1)}>
                    Retry
                </Button>
            </Card>
        );
    }
    if (!data) return <Flex justify="center" align="center" minH="400px"><Text color="gray.500">No data available</Text></Flex>;
    if (!data.computed) {
        return (
            <Card mt={4} textAlign="center" py={12}>
                <Icon as={FiActivity} boxSize={8} color="gray.300" />
                <Text mt={3} fontWeight="600">No interactions yet</Text>
                <Text fontSize="sm" color="gray.500">This contact has no tracked events.</Text>
            </Card>
        );
    }

    const donutOptions = {
        chart: { type: 'pie', height: 260 }, title: { text: null }, credits: { enabled: false },
        tooltip: { pointFormat: '<b>{point.y}</b> events ({point.percentage:.1f}%)' },
        plotOptions: { pie: { innerSize: '62%', dataLabels: { enabled: false }, borderWidth: 2 } },
        legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '11px' } },
        series: [{ name: 'Events', data: bySource.map((s, i) => ({ name: humanize(s.source), y: s.count, color: srcColor(s.source, i) })), showInLegend: true }],
    };
    const sessions = data.sessions || [];

    return (
        <VStack align="stretch" spacing={5} mt={4}>
            {/* ── KPI strip (Total Events · sources · Sessions · First/Last Activity) ── */}
            <Box overflowX="auto"><HStack spacing={4} align="stretch" minW="max-content">
                <Kpi label="Total Events" value={fmtInt(total)} sub={mom != null ? `${mom >= 0 ? '+' : ''}${mom}% vs last month` : 'All time'} subColor={mom != null ? (mom >= 0 ? 'green.500' : 'red.500') : undefined} />
                {bySource.slice(0, 5).map((s) => (
                    <Kpi key={s.source} label={humanize(s.source)} value={fmtInt(s.count)} sub={`${s.percent}% of total`} />
                ))}
                <Kpi label="Sessions" value={fmtInt(data.total_sessions)} sub="Distinct sessions" />
                <Kpi label="First Activity" value={fmtDate(data.first_activity)} sub="First seen" />
                <Kpi label="Last Activity" value={fmtDate(data.last_activity)} sub="Recent visit" />
            </HStack></Box>

            {/* ── Event Distribution by Source + Touchpoint Performance ── */}
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1.15fr' }} gap={4}>
                <Card>
                    <SectionHead title="Event Distribution by Source" sub="Breakdown of events across touchpoints" />
                    <HighchartsReact highcharts={Highcharts} options={donutOptions} />
                </Card>
                <Card>
                    <SectionHead title="Touchpoint Performance" sub="Event count and engagement share" />
                    <VStack align="stretch" spacing={3}>
                        {bySource.map((s, i) => (
                            <Box key={s.source}>
                                <Flex justify="space-between" align="center" mb={1}>
                                    <HStack spacing={2}><Box w="9px" h="9px" borderRadius="full" bg={srcColor(s.source, i)} /><Text fontSize="sm" fontWeight="600">{humanize(s.source)}</Text></HStack>
                                    <HStack spacing={4}><Text fontSize="sm" fontWeight="700">{fmtInt(s.count)}</Text><Text fontSize="xs" color="gray.400" w="42px" textAlign="right">{s.percent}%</Text></HStack>
                                </Flex>
                                <Progress value={s.percent} size="sm" borderRadius="full" bg="gray.100" sx={{ '& > div': { background: srcColor(s.source, i) } }} />
                                <Text fontSize="11px" color="gray.400" mt={1}>Last: {fmtDate(s.last)}</Text>
                            </Box>
                        ))}
                    </VStack>
                </Card>
            </Grid>

            {/* ── Event Analytics table ── */}
            <Card>
                <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3} mb={4}>
                    <Box>
                        <Text fontWeight="700" fontSize="md">Event Analytics</Text>
                        <Text fontSize="sm" color="gray.500">Detailed breakdown of all captured events</Text>
                    </Box>
                    <HStack spacing={2}>
                        <InputGroup maxW="230px" size="sm">
                            <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
                            <Input placeholder="Search events..." value={q} onChange={(e) => setQ(e.target.value)} borderRadius="md" />
                        </InputGroup>
                        <Select size="sm" maxW="150px" borderRadius="md" value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)}>
                            {sources.map((s) => <option key={s} value={s}>{s === 'all' ? 'All Sources' : humanize(s)}</option>)}
                        </Select>
                    </HStack>
                </Flex>
                <Box overflowX="auto"><Box minW="720px">
                    <Grid templateColumns="2fr 1fr 0.8fr 0.9fr 1.6fr 1fr" px={2} pb={2} borderBottomWidth="1px" borderColor="gray.100">
                        {['Event Name', 'Source', 'Count', 'Sessions', 'Last Triggered', 'Conversion'].map((h) => (
                            <Text key={h} fontSize="xs" color="gray.500" fontWeight="600">{h}</Text>
                        ))}
                    </Grid>
                    {tableRows.map((e, i) => (
                        <Grid key={`${e.event}-${e.source}`} templateColumns="2fr 1fr 0.8fr 0.9fr 1.6fr 1fr" px={2} py={3} alignItems="center" borderBottomWidth="1px" borderColor="gray.50">
                            <HStack spacing={2}><Box w="8px" h="8px" borderRadius="full" bg={PALETTE[i % PALETTE.length]} /><Text fontSize="sm" fontWeight="600">{humanize(e.event)}</Text></HStack>
                            <Box><Badge variant="outline" colorScheme="gray" borderRadius="md" fontSize="10px" textTransform="none">{humanize(e.source)}</Badge></Box>
                            <Text fontSize="sm" fontWeight="700">{fmtInt(e.count)}</Text>
                            <Text fontSize="sm" color="gray.600">{fmtInt(e.sessions)}</Text>
                            <Text fontSize="sm" color="gray.600">{fmtDateTime(e.last_triggered)}</Text>
                            <Box><Badge colorScheme={e.conversion ? 'green' : 'gray'} borderRadius="full" fontSize="10px" px={2}>{e.conversion ? 'Yes' : 'No'}</Badge></Box>
                        </Grid>
                    ))}
                    {tableRows.length === 0 && <Text fontSize="sm" color="gray.400" py={6} textAlign="center">No events match your filter.</Text>}
                </Box></Box>
            </Card>

            {/* ── Session Journey Explorer ── */}
            <Card>
                <SectionHead title="Session Journey Explorer" sub="Analyze user journeys and behavior patterns across sessions" />
                {sessions.length === 0 ? (
                    <Flex direction="column" align="center" justify="center" py={10} bg="gray.50" borderRadius="lg">
                        <Icon as={FiActivity} boxSize={7} color="gray.300" />
                        <Text mt={3} fontWeight="600" color="gray.600">No session data for this contact</Text>
                        <Text fontSize="sm" color="gray.500" maxW="420px" textAlign="center" mt={1}>
                            Session tracking (<code>session_id</code>) isn’t being captured for these events, so per-session journeys can’t be shown.
                        </Text>
                    </Flex>
                ) : (
                    <Grid templateColumns={{ base: '1fr', md: '320px 1fr' }} gap={5}>
                        {/* Left — session cards */}
                        <VStack align="stretch" spacing={3} maxH="440px" overflowY="auto" pr={1}>
                            {sessions.map((sess, i) => {
                                const dur = (parseDT(sess.end) - parseDT(sess.start)) / 1000;
                                const dev = deviceOf(sess.ua);
                                const selected = i === activeSession;
                                return (
                                    <Box key={sess.id} onClick={() => setActiveSession(i)} cursor="pointer"
                                        borderWidth="1px" borderRadius="lg" p={3}
                                        borderColor={selected ? 'blue.400' : 'gray.200'} bg={selected ? 'blue.50' : 'white'}>
                                        <Flex justify="space-between" align="center" mb={1}>
                                            <Text fontSize="11px" color="gray.500" fontFamily="mono">{sess.id.slice(0, 20)}</Text>
                                            <Badge variant="outline" colorScheme="gray" borderRadius="md" fontSize="10px" textTransform="none"><HStack spacing={1}><Icon as={FiUser} boxSize={2.5} /><span>Known</span></HStack></Badge>
                                        </Flex>
                                        <HStack spacing={2} mb={1}><Icon as={dev.icon} boxSize={3.5} color="gray.500" /><Text fontSize="sm" fontWeight="700">{dev.label}</Text></HStack>
                                        <Flex justify="space-between" color="gray.500" fontSize="xs">
                                            <HStack spacing={1}><Icon as={FiClock} boxSize={3} /><Text>{fmtDur(dur)}</Text></HStack>
                                            <HStack spacing={1}><Icon as={FiActivity} boxSize={3} /><Text>{fmtInt(sess.events)} events</Text></HStack>
                                        </Flex>
                                        <Text fontSize="xs" color="gray.500" mt={1}>{fmtDateTime(sess.start)}</Text>
                                        {sess.converted ? (
                                            <Badge mt={2} colorScheme="green" borderRadius="md" fontSize="10px" px={2}>✅ Converted</Badge>
                                        ) : sess.has_cart ? (
                                            <Badge mt={2} colorScheme="orange" borderRadius="md" fontSize="10px" px={2}>⚠️ Abandoned at cart</Badge>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </VStack>
                        {/* Right — session timeline */}
                        <Box>
                            {(() => {
                                const sess = sessions[activeSession];
                                const dev = deviceOf(sess.ua);
                                const dur = (parseDT(sess.end) - parseDT(sess.start)) / 1000;
                                const evts = sessionTimelines[sess.id] || [];
                                return (
                                    <>
                                        <Text fontWeight="700" mb={2}>Session Timeline</Text>
                                        <HStack spacing={4} color="gray.500" fontSize="xs" mb={4} flexWrap="wrap">
                                            <HStack spacing={1}><Icon as={dev.icon} boxSize={3.5} /><Text>{dev.label}</Text></HStack>
                                            <HStack spacing={1}><Icon as={FiClock} boxSize={3.5} /><Text>{fmtDur(dur)}</Text></HStack>
                                            <HStack spacing={1}><Icon as={FiActivity} boxSize={3.5} /><Text>{fmtInt(sess.events)} events</Text></HStack>
                                            <HStack spacing={1}><Icon as={FiCalendar} boxSize={3.5} /><Text>{fmtDateTime(sess.start)}</Text></HStack>
                                        </HStack>
                                        {evts.length === 0 ? (
                                            <Text fontSize="sm" color="gray.400" py={4}>Timeline detail not available for this session.</Text>
                                        ) : (
                                            <VStack align="stretch" spacing={0}>
                                                {evts.map((ev, j) => (
                                                    <Flex key={j} gap={3} pb={4} position="relative">
                                                        <Flex direction="column" align="center">
                                                            <Flex w="30px" h="30px" borderRadius="full" bg="gray.50" align="center" justify="center" flexShrink={0}>
                                                                <Icon as={FiGlobe} boxSize={3.5} color={PALETTE[j % PALETTE.length]} />
                                                            </Flex>
                                                            {j < evts.length - 1 && <Box flex={1} w="2px" bg="gray.100" my={1} />}
                                                        </Flex>
                                                        <Box flex={1} pt={1}>
                                                            <Flex justify="space-between">
                                                                <Text fontSize="sm" fontWeight="600">{humanize(ev.event)}</Text>
                                                                <Text fontSize="xs" color="gray.400">{fmtTime(ev.date_time)}</Text>
                                                            </Flex>
                                                            <Badge mt={1} variant="outline" colorScheme="gray" borderRadius="md" fontSize="10px" textTransform="none">{humanize(ev.source)}</Badge>
                                                        </Box>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        )}
                                        {/* Outcome banner — driven by the real conversion signal on the session. */}
                                        {sess.converted ? (
                                            <Flex mt={2} bg="green.50" borderWidth="1px" borderColor="green.100" borderRadius="lg" p={3} align="center" gap={2}>
                                                <Icon as={FiCheckCircle} color="green.500" boxSize={4} />
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="600" color="green.700">Conversion Achieved</Text>
                                                    <Text fontSize="xs" color="green.600">Session resulted in successful conversion</Text>
                                                </Box>
                                            </Flex>
                                        ) : (
                                            <Flex mt={2} bg={sess.has_cart ? 'orange.50' : 'gray.50'} borderWidth="1px" borderColor={sess.has_cart ? 'orange.100' : 'gray.100'} borderRadius="lg" p={3} align="center" gap={2}>
                                                <Icon as={FiActivity} color={sess.has_cart ? 'orange.500' : 'gray.400'} boxSize={4} />
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="600" color={sess.has_cart ? 'orange.700' : 'gray.700'}>{sess.has_cart ? 'Abandoned at cart' : 'No conversion'}</Text>
                                                    <Text fontSize="xs" color={sess.has_cart ? 'orange.600' : 'gray.500'}>{fmtInt(sess.events)} events over {fmtDur(dur)}</Text>
                                                </Box>
                                            </Flex>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                    </Grid>
                )}
            </Card>
        </VStack>
    );
}
